import { Alert, Button } from '@mui/material';
import React, { useState } from 'react';
import { useNavigate } from 'react-router';
import { useParams } from 'react-router-dom';
import { createTestCycle } from '../../../modules/testcycles';
import ConfirmationModal from '../../elements/ConfirmationModal';
import Modal from '../../elements/Modal';
import CreateTestCycleForm from './CreateTestCycleForm';
import { checkRequiredFieldsInCycle } from '../../../lib/executionUtils';
import useExecutionScope from '../../../hooks/useExecutionScope';
import ExecutionScopeAlert from '../../elements/common/execution/ExecutionScopeAlert';
const AddTestCycleModal = ({
  showModal,
  handleClose,
  statuses,
  purposeOptions,
  typeOptions,
  projectUsers,
  jobs,
  jobError,
  addNotification,
  fetchTestCycles,
}) => {
  const defaultFormData = {
    name: '',
    description: '',
    startDate: '',
    endDate: '',
    status: '',
    owner: '',
    releaseVersion: '',
    type: '',
    purpose: '',
    job: '',
    geographies: [],
    languages: [],
    cloudDevice: [],
    platforms: [],
    subPlatforms: [],
    browsers: [],
  };
  const [formData, setFormData] = useState(defaultFormData);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [responseMessage, setResponseMessage] = useState(null);
  const navigate = useNavigate();
  const { projectId } = useParams();
  const { isScopeDefined, isPlatformsDefined } = useExecutionScope(projectId);
  const [submitting, setSubmitting] = useState(false);

  const handleFormSubmit = async () => {
    setSubmitting(true);
    try {
      if (isPlatformsDefined) {
        const errorMessage = checkRequiredFieldsInCycle(formData);
        if (errorMessage) {
          setResponseMessage(errorMessage);
          setSubmitting(false);
          return;
        }
      }
      const testcycle = {
        ...formData,
        startDate: formData.startDate || new Date(),
        endDate: formData.endDate || new Date(),
        type: formData.type || 'Manual',
      };
      const response = await createTestCycle({ testcycle }, projectId);
      if (response && response?.success === false) {
        setResponseMessage(response?.message);
        setSubmitting(false);
        return;
      }
      addNotification(
        'success',
        'Test cycle created successfully! Redirecting to test case associations...'
      );
      setFormData(defaultFormData);
      fetchTestCycles();
      navigate(
        `/project/${projectId}/testcycles/${response?.test_cycle?.key}?type=testcases`
      );
    } catch (error) {
      const errorMessage =
        error?.message || 'Failed to create test cycle. Please try again.';
      addNotification('danger', errorMessage);
    }
    setSubmitting(false);
  };

  const handleCloseModal = () => {
    if (formData.name || formData.description) {
      setShowConfirmationModal(true);
    } else {
      handleDiscard();
    }
  };

  const handleDiscard = () => {
    setShowConfirmationModal(false);
    handleClose();
    setFormData(defaultFormData);
    setResponseMessage(null);
  };

  return (
    <>
      <Modal
        open={showModal}
        handleClose={handleCloseModal}
        modalTitle={'Create Test Cycle'}
        action={() => (
          <>
            <Button
              variant="outlined"
              color="secondary"
              disabled={submitting}
              onClick={handleCloseModal}>
              Close
            </Button>
            <Button
              variant="contained"
              onClick={handleFormSubmit}
              disabled={!formData?.name || !formData?.purpose || submitting}>
              Create
            </Button>
          </>
        )}>
        {!isPlatformsDefined && <ExecutionScopeAlert projectId={projectId} />}
        <CreateTestCycleForm
          formData={formData}
          isEditing={true}
          setFormData={setFormData}
          setResponseMessage={setResponseMessage}
          statuses={statuses}
          purposeOptions={purposeOptions}
          typeOptions={typeOptions}
          jobs={jobs}
          projectUsers={projectUsers}
          isScopeDefined={isScopeDefined}
        />
        {formData?.type === 'Automation' && jobError && (
          <Alert severity="error" className="mt-3">
            {jobError}
          </Alert>
        )}
        {responseMessage && (
          <Alert severity="error" className="mt-3">
            {responseMessage}
          </Alert>
        )}
      </Modal>
      <ConfirmationModal
        show={showConfirmationModal}
        handleClose={() => setShowConfirmationModal(false)}
        handleConfirm={handleDiscard}
        title="Discard"
        body="Are you sure you want to cancel? Any unsaved changes will be lost."
        buttonLabel="Discard"
        buttonLabelSecondary="Go Back"
        buttonStyle="primary"
      />
    </>
  );
};

export default AddTestCycleModal;
